import { useCallback, useMemo } from "react";
import { useFetchMeQuery } from "features/auth/authAPI";

/**
 * Hook to check debtor tag add/remove permissions based on user's groups.
 *
 * Permission rules:
 * - If addableByGroupIds is empty/null, ALL users can add the tag
 * - If addableByGroupIds has values, only users in those groups can add it
 * - If removableByGroupIds is empty/null, ALL users can remove the tag
 * - If removableByGroupIds has values, only users in those groups can remove it
 * - Admin users (me.isAdmin) bypass all permission checks
 *
 * @returns {{ canAddTag: (tag) => boolean, canRemoveTag: (tag) => boolean, filterAddableTags: (tags) => tags, filterRemovableTags: (tags) => tags }}
 */
export function useDebtorTagPermissions() {
  const { data: me } = useFetchMeQuery();

  const userGroupIds = useMemo(() => me?.groupIds ?? [], [me?.groupIds]);
  const isAdmin = me?.isAdmin ?? false;

  /**
   * Check if user can add a debtor tag
   * @param {{ addableByGroupIds?: string[] }} tag - The debtor tag
   * @returns {boolean}
   */
  const canAddTag = useCallback(
    (tag) => {
      if (isAdmin) return true;
      const addableGroups = tag?.addableByGroupIds;
      if (!addableGroups || addableGroups.length === 0) return true;
      return addableGroups.some((groupId) => userGroupIds.includes(groupId));
    },
    [isAdmin, userGroupIds],
  );

  /**
   * Check if user can remove a debtor tag
   * @param {{ removableByGroupIds?: string[] }} tag - The debtor tag
   * @returns {boolean}
   */
  const canRemoveTag = useCallback(
    (tag) => {
      if (isAdmin) return true;
      const removableGroups = tag?.removableByGroupIds;
      if (!removableGroups || removableGroups.length === 0) return true;
      return removableGroups.some((groupId) => userGroupIds.includes(groupId));
    },
    [isAdmin, userGroupIds],
  );

  /**
   * Filter debtor tags to only include those the user can add
   * @param {Array} tags - Array of debtor tags
   * @returns {Array} - Filtered array of addable tags
   */
  const filterAddableTags = useCallback(
    (tags) => {
      if (!tags) return [];
      return tags.filter(canAddTag);
    },
    [canAddTag],
  );

  /**
   * Filter debtor tags to only include those the user can remove
   * @param {Array} tags - Array of debtor tags
   * @returns {Array} - Filtered array of removable tags
   */
  const filterRemovableTags = useCallback(
    (tags) => {
      if (!tags) return [];
      return tags.filter(canRemoveTag);
    },
    [canRemoveTag],
  );

  return {
    canAddTag,
    canRemoveTag,
    filterAddableTags,
    filterRemovableTags,
  };
}

export default useDebtorTagPermissions;
